/**
 * Slider Block
 * Self-contained slider component that can be used in any section.
 * Children of the slider block become slides.
 */

// Track initialized sliders to prevent duplicates
const initializedSliders = new WeakSet();

// Queue for sliders waiting for Swiper to load
const pendingInitializations = [];

// Store instances for resize handling
const sliderInstances = new Map();

// Breakpoint for mobile/desktop (matches Tailwind md:)
const MOBILE_BREAKPOINT = 768;

function getSliderIdToken(sliderId) {
  if (!sliderId) return null;
  const marker = '__slider_';
  const markerIndex = sliderId.lastIndexOf(marker);
  return markerIndex >= 0 ? sliderId.slice(markerIndex + marker.length) : sliderId;
}

function getSliderInstanceByBlockId(blockId) {
  if (!blockId) return null;
  for (const [element, instance] of sliderInstances.entries()) {
    if (element?.dataset?.blockId === blockId) {
      return instance;
    }
  }
  return null;
}

function sliderIdsMatch(sliderBlockId, controlSliderId) {
  if (!sliderBlockId || !controlSliderId) return false;
  if (sliderBlockId === controlSliderId) return true;
  const blockToken = getSliderIdToken(sliderBlockId);
  const controlToken = getSliderIdToken(controlSliderId);
  return Boolean(blockToken && controlToken && blockToken === controlToken);
}

class SliderBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.originalSlides = null;
    this.isInitialized = false;

    // Prevent duplicate instance creation
    if (initializedSliders.has(element)) {
      return;
    }
    initializedSliders.add(element);

    // Store visibility setting
    this.visibility = this.element.dataset.sliderVisibility || 'both';

    // Store instance for resize handling
    sliderInstances.set(element, this);

    // Check if should init based on visibility
    this.checkVisibilityAndInit();

    // Listen for resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Check if slider should be active based on visibility setting and viewport
   */
  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  /**
   * Check visibility and init/destroy accordingly
   */
  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
    }, 150);
  }

  /**
   * Initializes the Swiper slider
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingInitializations.push(this);
      return;
    }

    // Skip elements managed by recently-viewed.js
    if (this.element.closest('recently-viewed')) {
      return;
    }

    // Content container
    this.contentEl = this.element.querySelector('.slider-content');
    if (!this.contentEl) {
      return;
    }

    const blockId = this.element.dataset.blockId || null;
    const section = this.element.closest('[id^="shopify-section-"]');

    // Find and preserve control elements before clearing
    // First try within the slider (nested), then try in parent section (separate blocks)
    let controlsElement = this.element.querySelector('[data-slider-control]');
    const nestedControlSliderId = controlsElement?.dataset?.sliderId;

    // Ignore nested controls explicitly linked to a different slider.
    if (controlsElement && nestedControlSliderId && !sliderIdsMatch(blockId, nestedControlSliderId)) {
      controlsElement = null;
    }
    
    if (!controlsElement && blockId) {
      // Look for controls in the same section that target this slider
      if (section) {
        controlsElement = section.querySelector(`[data-slider-control][data-slider-id="${blockId}"]`);
        if (!controlsElement) {
          const linkedControls = [...section.querySelectorAll('[data-slider-control][data-slider-id]')];
          controlsElement = linkedControls.find(control =>
            sliderIdsMatch(blockId, control.dataset.sliderId)
          ) || null;
        }
      }
      
      // If still not found, try any controls without a specific slider ID in the section
      if (!controlsElement) {
        const allControls = section?.querySelectorAll('[data-slider-control]:not([data-slider-id])');
        if (allControls && allControls.length === 1) {
          // Only auto-match if there's exactly one slider and one unassigned controls block
          const allSliders = section?.querySelectorAll('[data-slider-block]');
          if (allSliders && allSliders.length === 1) {
            controlsElement = allControls[0];
          }
        }
      }
    }

    // Final safety guard: never bind controls targeted at a different slider ID.
    if (controlsElement && controlsElement.dataset.sliderId && !sliderIdsMatch(blockId, controlsElement.dataset.sliderId)) {
      controlsElement = null;
    }

    const parentSlide = this.element.closest('.swiper-slide');
    const isNestedSlider = Boolean(parentSlide && parentSlide.closest('[data-slider-block]'));
    const isHeaderDrawerSlider = Boolean(this.element.closest('header-drawer-menu'));

    let slides;
    
    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      // Direct children become slides (filter out style, script, controls, and other non-content elements)
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter(el => 
        !excludedTags.includes(el.tagName) && 
        !el.hasAttribute('data-slider-control')
      );

      if (slides.length < 1) {
        return;
      }

      this.originalSlides = slides;
    }

    // Create Swiper markup
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('swiper', 'slider-block-swiper', 'w-full', 'max-w-full');
    
    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-x-hidden');
    }
    
    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move children into Swiper wrapper as slides
    let hasHoverLift = false;
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      if (slide.querySelector('[data-hover-lift]')) hasHoverLift = true;
      wrapperEl.appendChild(slide);
    });

    if (hasHoverLift) {
      swiperEl.style.overflow = 'visible';
      swiperEl.style.overflowX = 'clip';
    }

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(swiperEl);
    
    // Re-append controls if they were nested inside the slider
    // (If controls are outside in the section, they stay where they are)
    const wasNested = controlsElement?.parentElement === this.contentEl;
    if (controlsElement && wasNested) {
      this.element.appendChild(controlsElement);
    }

    // Get options from data attribute
    const customOptions = this.element.dataset.swiperOptions 
      ? JSON.parse(this.element.dataset.swiperOptions) 
      : {};

    // Keep wheel scroll on top-level sliders; nested sliders should own wheel
    // interaction while hovered to avoid parent slider stealing input.
    if (isNestedSlider) {
      const nestedMousewheelOptions = typeof customOptions.mousewheel === 'object' && customOptions.mousewheel !== null
        ? customOptions.mousewheel
        : {};

      customOptions.mousewheel = {
        enabled: true,
        forceToAxis: true,
        sensitivity: 1,
        thresholdDelta: 4,
        releaseOnEdges: false,
        ...nestedMousewheelOptions,
      };
    }

    // Default options
    const defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 16,
      // Enable touch/drag interactions
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      // Improve touch responsiveness
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      threshold: 5,
      touchMoveStopPropagation: false,
      touchStartPreventDefault: false,
      touchStartForcePreventDefault: false,
      touchReleaseOnEdges: false,
    };

    const customPaginationColour = this.element.dataset.paginationColour;
    const hasAutoplayEnabled = Boolean(customOptions.autoplay);
    const hasLoopEnabled = customOptions.loop === true;
    const shouldUseEdgeNavigationStyles = Boolean(controlsElement) && !hasLoopEnabled && !hasAutoplayEnabled;

    if (controlsElement) {
      controlsElement.classList.toggle('slider-nav-edge-aware', shouldUseEdgeNavigationStyles);
    }

    if (shouldUseEdgeNavigationStyles) {
      // Ensure real start/end states exist when loop setting is not enabled.
      customOptions.loop = false;
    }

    // Only enable loop if there are more slides than the max slidesPerView
    const desktopPerView = customOptions.breakpoints?.['768']?.slidesPerView || customOptions.slidesPerView || defaultOptions.slidesPerView;
    const mobilePerView = customOptions.slidesPerView || defaultOptions.slidesPerView;
    const maxPerView = Math.max(Number(desktopPerView) || 1, Number(mobilePerView) || 1);
    if (slides.length > maxPerView && customOptions.loop !== false) {
      defaultOptions.loop = true;
    }

    // Mobile drawer sliders should not loop: clone wrap causes perceived reordering
    // while users vertically scroll drawer content.
    if (isHeaderDrawerSlider) {
      customOptions.loop = false;
    }

    // Add navigation selectors (blockId already defined earlier)
    if (blockId && customOptions.navigation !== false) {
      if (controlsElement) {
        // Controls exist and are already validated/matched above.
        // Always pass the concrete elements to avoid selector/ID mismatch.
        const nextBtn = controlsElement.querySelector('.slider-navigation-next');
        const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
        if (nextBtn && prevBtn) {
          customOptions.navigation = {
            nextEl: nextBtn,
            prevEl: prevBtn,
          };
        }
      }
    }

    // Check if slider-controls block is present (already found earlier)
    let controlsPaginationColour = customPaginationColour;
    
    if (controlsElement) {
      // Find pagination element
      const paginationEl = controlsElement.querySelector('.swiper-pagination');
      if (paginationEl) {
        // Get pagination type and colour from data attributes
        const paginationType = paginationEl.dataset.paginationType || 'bullets';
        controlsPaginationColour = paginationEl.dataset.paginationColour || customPaginationColour;
        
        // Override pagination config
        customOptions.pagination = {
          el: paginationEl,
          type: paginationType,
          clickable: true,
        };
        
        // Handle custom pagination type - render as styled bars
        if (paginationType === 'custom') {
          customOptions.pagination.renderCustom = (swiper, current, total) => {
            let bullets = '';
            for (let i = 1; i <= total; i++) {
              const isActive = i === current ? 'opacity-100' : 'opacity-50';
              bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${controlsPaginationColour};" data-slide-index="${i}"></span>`;
            }
            return `<div class="flex gap-2 justify-center">${bullets}</div>`;
          };

          // Make custom bullets clickable
          this.element.addEventListener('click', (e) => {
            const bullet = e.target.closest('[data-slide-index]');
            if (bullet && this.swiper) {
              const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
              this.swiper.slideTo(index);
            }
          });
        }
      }
    } else {
      // Fallback: Handle custom pagination type when no controls block (old behavior)
      if (customOptions.pagination && customOptions.pagination.type === 'custom') {
        customOptions.pagination.renderCustom = (swiper, current, total) => {
          let bullets = '';
          for (let i = 1; i <= total; i++) {
            const isActive = i === current ? 'opacity-100' : 'opacity-50';
            bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${customPaginationColour};" data-slide-index="${i}"></span>`;
          }
          return `<div class="flex gap-2 justify-center">${bullets}</div>`;
        };

        // Make custom bullets clickable
        this.element.addEventListener('click', (e) => {
          const bullet = e.target.closest('[data-slide-index]');
          if (bullet && this.swiper) {
            const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
            this.swiper.slideTo(index);
          }
        });
      }
    }

    // Initialize Swiper
    this.swiper = new Swiper(swiperEl, {
      ...defaultOptions,
      ...customOptions,
    });

    this.element.addEventListener('wheel', (event) => {
      if (event._sliderForwarded) return;
      const targetClass = event.target?.className || '';
      const looksLikeOuterHoverOverlay = typeof targetClass === 'string' && targetClass.includes('slider-nav-hover');
      const listenerBlockId = this.element.dataset.blockId || null;

      if (!isNestedSlider && looksLikeOuterHoverOverlay) {
        const sliderBlocksUnderPointer = typeof document.elementsFromPoint === 'function'
          ? document
            .elementsFromPoint(event.clientX, event.clientY)
            .map(el => el.closest?.('[data-slider-block]')?.dataset?.blockId || null)
            .filter(Boolean)
          : [];
        const firstUnderlyingSliderBlock = sliderBlocksUnderPointer.find(blockId => blockId !== listenerBlockId) || null;

        if (firstUnderlyingSliderBlock) {
          const underlyingInstance = getSliderInstanceByBlockId(firstUnderlyingSliderBlock);
          if (underlyingInstance?.swiperEl) {
            const syntheticWheel = new WheelEvent('wheel', {
              deltaX: event.deltaX,
              deltaY: event.deltaY,
              deltaMode: event.deltaMode,
              clientX: event.clientX,
              clientY: event.clientY,
              bubbles: true,
              cancelable: true,
            });
            syntheticWheel._sliderForwarded = true;
            underlyingInstance.swiperEl.dispatchEvent(syntheticWheel);
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
            return;
          }
        }
      }
    }, { capture: true, passive: false });

    this.element.addEventListener('wheel', (event) => {
      const eventSliderBlock = event.target?.closest?.('[data-slider-block]')?.dataset?.blockId || null;
      const listenerBlockId = this.element.dataset.blockId || null;
      const shouldStopForNestedHover = isNestedSlider && eventSliderBlock === listenerBlockId;

      if (shouldStopForNestedHover) {
        event.stopPropagation();
      }
    }, { passive: false });

    this.isInitialized = true;
  }

  /**
   * Destroys the Swiper instance and restores original markup
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.swiperEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      this.originalSlides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        this.contentEl.appendChild(slide);
      });
    }

    this.swiperEl = null;
    this.isInitialized = false;
  }

  /**
   * Full cleanup including event listeners
   */
  cleanup() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

/**
 * Initialize all slider blocks on the page
 */
function initSliderBlocks() {
  const sliders = document.querySelectorAll('[data-slider-block]');
  sliders.forEach(slider => {
    new SliderBlock(slider);
  });
}

/**
 * Process pending initializations once Swiper is available
 */
function processPendingInitializations() {
  if (!window.Swiper || pendingInitializations.length === 0) return;
  
  while (pendingInitializations.length > 0) {
    const instance = pendingInitializations.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingInitializations();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value
      });
      processPendingInitializations();
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSliderBlocks();
    waitForSwiper();
  });
} else {
  initSliderBlocks();
  waitForSwiper();
}

// Re-initialize on back/forward navigation
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initSliderBlocks();
  }
});

// Export for potential external use
window.SliderBlock = SliderBlock;
window.initSliderBlocks = initSliderBlocks;

